<template>
  <div
    id="app"
    :class="[
      'min-h-screen bg-fixed bg-cover bg-center',
      isAdminRoute ? 'bg-gray-100' : 'bg-[radial-gradient(circle_at_20%_35%,rgba(217,70,239,0.1)_0%,transparent_40%),radial-gradient(circle_at_80%_10%,rgba(168,85,247,0.12)_0%,transparent_40%),radial-gradient(circle_at_50%_100%,rgba(99,102,241,0.15)_0%,transparent_50%)]'
    ]"
  >
    <div v-if="!isAdminRoute && !consentAsked" class="fixed bottom-4 inset-x-0 px-4 z-50">
      <div class="mx-auto max-w-3xl glass-card p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <p class="text-sm text-gray-200">
          {{ $t('common.analytics_consent_question') }}
        </p>
        <div class="flex gap-2">
          <button class="btn-secondary" @click="denyConsent">
            {{ $t('common.analytics_consent_deny') }}
          </button>
          <button class="btn-primary" @click="grantConsent">
            {{ $t('common.analytics_consent_allow') }}
          </button>
        </div>
      </div>
    </div>
    <!-- Only show header for non-admin routes (some pages may hide header via route meta) -->
    <AppHeader v-if="!isAdminRoute && !route.meta?.hideHeader" :header-color="route.meta?.headerColor" />
    
    <!-- Main content -->
    <main class="min-h-screen">
      <RouterView />
    </main>
    
    <!-- Only show footer for non-admin routes (some pages may change bg footer via route meta)-->
    <AppFooter v-if="!isAdminRoute" :footer-color="route.meta?.footerColor" />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import { hasAnalyticsConsent, setAnalyticsConsent } from '@/services/analytics'
import { userNotificationSocketService, type TicketCheckInSuccessEvent } from '@/services/userNotificationSocket'
import { useUserNotificationsStore } from '@/stores/user-notifications'
import { toast } from '@/utils/toast'
import { useReleaseFeatureFlags } from '@/composables/useReleaseFeatureFlags'

const route = useRoute()
const authStore = useAuthStore()
const userNotificationsStore = useUserNotificationsStore()
const { showMarketplaceNav } = useReleaseFeatureFlags()
const TICKET_CHECKIN_SUCCESS_EVENT = 'ticket-checkin-success'

const extractTicketIdFromActionUrl = (actionUrl?: string) => {
  if (!actionUrl) return undefined
  const match = actionUrl.match(/ticketId=([^&]+)/)
  if (match?.[1]) return decodeURIComponent(match[1])
  return undefined
}

// Computed
const isAdminRoute = computed(() => {
  return route.path.startsWith('/admin')
})

const consentAsked = ref(true)
const refreshConsent = () => {
  consentAsked.value = hasAnalyticsConsent() || localStorage.getItem('analytics_consent') === 'denied'
}

const grantConsent = () => {
  setAnalyticsConsent(true)
  refreshConsent()
}

const denyConsent = () => {
  setAnalyticsConsent(false)
  refreshConsent()
}

const handleTicketCheckInSuccess = async (payload: TicketCheckInSuccessEvent) => {
  const isNewRealtimeEvent = userNotificationsStore.handleRealtimeEvent({
    notificationId: payload.notificationId,
    messageId: (payload as TicketCheckInSuccessEvent & { messageId?: string }).messageId,
    timestamp: payload.timestamp,
    type: payload.type,
    ticketId: payload.ticketId,
  })

  if (!isNewRealtimeEvent) {
    return
  }

  toast.success(payload.message || 'Vé của bạn đã được check-in thành công.')

  window.dispatchEvent(new CustomEvent(TICKET_CHECKIN_SUCCESS_EVENT, {
    detail: {
      notificationId: payload.notificationId,
      ticketId: payload.ticketId || extractTicketIdFromActionUrl(payload.actionUrl),
    },
  }))
}

let unsubscribeTicketCheckInSocket: (() => void) | null = null

onMounted(() => {
  // Initialize auth state from localStorage
  authStore.initializeAuth()
  if (!hasAnalyticsConsent() && localStorage.getItem('analytics_consent') !== 'denied') {
    consentAsked.value = false
  }

  if (showMarketplaceNav.value) { // [SOCKET_CONTROL]: Hidden for release
    unsubscribeTicketCheckInSocket = userNotificationSocketService.onTicketCheckInSuccess((payload) => {
      void handleTicketCheckInSuccess(payload)
    })
  }

  if (authStore.isAuthenticated) {
    void userNotificationsStore.initializeForCurrentUser()
  }
})

watch(
  () => authStore.user?.id,
  (userId, previousUserId) => {
    if (!userId) {
      userNotificationsStore.resetState()
      return
    }

    if (userId !== previousUserId) {
      void userNotificationsStore.initializeForCurrentUser()
    }
  },
)

onBeforeUnmount(() => {
  if (unsubscribeTicketCheckInSocket) {
    unsubscribeTicketCheckInSocket()
    unsubscribeTicketCheckInSocket = null
  }
})
</script>

<style scoped>
.glass-card { @apply bg-white/5 border border-white/10 rounded-xl; }
.btn-primary { @apply px-3 py-1.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors; }
.btn-secondary { @apply px-3 py-1.5 bg-gray-700/50 text-white rounded-lg hover:bg-white hover:text-gray-900 transition-colors; }
/* blue-500: 59,130,246
pink-400: 244,114,182
purple-500: 168,85,247
fuchsia-500: 217,70,239
indigo-500: 99,102,241
sky-200: 186,230,253
*/

/*
Gradient Background:
  3-orbs style:
    - radial-gradient(circle_at_50%_50%,rgba(244,114,182,0.15)_0%,rgba(244,114,182,0.06)_30%,rgba(244,114,182,0.03)_40%,transparent_60%)
    - radial-gradient(circle_at_5%_0%,rgba(217,70,239,0.24)_0%,rgba(217,70,239,0.08)_15%,rgba(217,70,239,0.04)_20%,transparent_30%)
    - radial-gradient(circle_at_95%_100%,rgba(168,85,247,0.24)_0%,rgba(168,85,247,0.08)_15%,rgba(168,85,247,0.04)_20%,transparent_30%)
  2-orbs style:
    - radial-gradient(circle_at_5%_0%,rgba(217,70,239,0.24)_0%,rgba(217,70,239,0.08)_20%,rgba(244,114,182,0.04)_30%,transparent_45%)
    - radial-gradient(circle_at_95%_100%,rgba(168,85,247,0.24)_0%,rgba(168,85,247,0.08)_20%,rgba(168,85,247,0.04)_30%,transparent_45%)
*/
</style>




